import React, { useRef, useEffect } from 'react'

// Components
import DocumentHead from 'components/DocumentHead'
import DocumentHeader from 'components/DocumentHeader'
import ErrorMessage from 'components/ErrorMessage'
import { handleFetchMore } from 'components/InfiniteList'

// Local Components
import DocumentItemsLayout from './DocumentItemsLayout'
import SharesEmptyState from './ShareEmptyState'
import SharesWithBanners from './SharesWithBanners'

// Types
import { useGetSharesQuery, WorkspaceMinimalFragment } from 'gqlTypes'

interface WorkspaceSharesListProps {
  workspaceId: string
  workspace?: WorkspaceMinimalFragment
  search?: string
  filter?: 'LIBRARY' | 'NO_PROJECT' | 'SHARED_WITH_ME' | null
}

// Helpers
const entriesPath = ['workspace', 'shares', 'entries']

const HEADER_COPY = {
  SHARED_WITH_ME: {
    title: 'Shared with Me',
    description: 'All documents that are directly shared with you.',
  },
  LIBRARY: {
    title: 'Libraries',
    description:
      'Documents containing Components that can be shared across other documents.',
  },
  NO_PROJECT: null,
}

const WorkspaceSharesList = ({
  workspaceId,
  search,
  filter = null,
  workspace,
}: WorkspaceSharesListProps) => {
  const workspaceHasDocuments = useRef<boolean>()

  const variables = {
    identifier: workspaceId,
    after: null,
    search: {
      name: search,
      filter,
    },
  }

  const result = useGetSharesQuery({
    variables,
    fetchPolicy: 'cache-and-network',
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.identifier !== curr?.identifier,
  })

  // We are keeping the totalCount in a ref
  // to see if the Workspace has documents
  // to control Banner logic even if a search
  // returns a totalCount of 0
  useEffect(() => {
    if (!search) {
      const totalCount = result?.data?.workspace?.shares?.meta?.totalCount
      workspaceHasDocuments.current = !!totalCount
    }
  }, [result, search])

  if (!result?.data && result?.error) {
    return <ErrorMessage.Generic />
  }

  const shares = result?.data?.workspace.shares.entries || []

  const after = result.data?.workspace.shares.meta.after || null
  const loadMoreHandler = handleFetchMore(result.fetchMore, entriesPath, {
    after,
  })

  const loading = result.networkStatus < 3
  const totalShareCount = result?.data?.workspace.shares.meta.totalCount || 0

  if (!loading && shares.length === 0) {
    return (
      <SharesEmptyState
        filter={filter}
        search={search}
        workspace={workspace!}
        workspaceHasDocuments={workspaceHasDocuments.current}
      />
    )
  }

  const headerCopy = filter && HEADER_COPY[filter]
  return (
    <>
      {!loading && <SharesWithBanners workspace={workspace!} />}
      {headerCopy && <DocumentHead title={headerCopy.title} />}
      <DocumentItemsLayout
        loading={loading}
        shares={shares}
        onLoadMore={loadMoreHandler}
        dropdownMenu="document"
        totalShareCount={totalShareCount}
        header={
          headerCopy && (
            <DocumentHeader
              title={headerCopy.title}
              description={headerCopy.description}
            />
          )
        }
        showProjectName
      />
    </>
  )
}

export default WorkspaceSharesList
